import { useParams, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { usePuzzle, useGameState, useSaveGameState } from '../hooks'
import { useAuthStore } from '../stores/authStore'
import { RingenGame } from '../../shared/RingenGame'
import Board from '../components/Board'
import { findMatchingMoves, type TilePosition } from '../utils/moveDetection'

export default function GamePage() {
  const { puzzleId } = useParams<{ puzzleId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const { data: puzzle, isLoading } = usePuzzle(puzzleId!)
  const { data: savedState } = useGameState(puzzleId!)
  const saveGameState = useSaveGameState()
  const [game, setGame] = useState<RingenGame | null>(null)
  const [board, setBoard] = useState<(any | null)[]>([])
  const [moveCount, setMoveCount] = useState(0)
  const [solved, setSolved] = useState(false)

  // Initialize game when puzzle loads
  useEffect(() => {
    if (puzzle) {
      const newGame = new RingenGame(puzzle)

      // Load saved state if available
      if (savedState && savedState.boardState) {
        try {
          newGame.board = [...savedState.boardState]
          newGame.moveCount = savedState.moveCount || 0
        } catch (error) {
          console.error('Failed to load saved state:', error)
          // Fall back to initial state
        }
      }

      setGame(newGame)
      setBoard([...newGame.board])
      setMoveCount(newGame.moveCount)
      setSolved(newGame.isSolved())
    }
  }, [puzzle, savedState])

  const handleBack = () => {
    navigate('/')
  }

  const handleReset = () => {
    if (game) {
      game.reset()
      setBoard([...game.board])
      setMoveCount(game.moveCount)
      setSolved(game.isSolved())
    }
  }

  const handleDragPath = (path: TilePosition[]) => {
    if (!game || !puzzle) return

    const topology = puzzle.topology ?? 'rect'
    const allowOuroboros = puzzle.allow_ouroboros ?? false
    const { moveIndices, clockwise, dynamicCycle } = findMatchingMoves(
      path, puzzle.moves, game, topology, allowOuroboros,
    )

    if (moveIndices.length === 1) {
      game.applyMove(moveIndices[0], clockwise)
    } else if (moveIndices.length > 1) {
      // Ambiguous drag - matched multiple moves, reject like iOS does
      // TODO: show visual feedback (flash)
      return
    } else if (dynamicCycle && dynamicCycle.length >= 3) {
      game.applyDynamicMove(dynamicCycle, clockwise)
    } else {
      return // No valid move
    }

    const newBoard = [...game.board]
    const newMoveCount = game.moveCount
    const newSolved = game.isSolved()

    setBoard(newBoard)
    setMoveCount(newMoveCount)
    setSolved(newSolved)

    // Auto-save state (only when logged in)
    if (isAuthenticated) {
      saveGameState.mutate({
        puzzleId: puzzleId!,
        boardState: newBoard,
        moveCount: newMoveCount,
        solved: newSolved,
      })
    }
  }

  if (isLoading || !puzzle || !game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-600 text-xl">Loading puzzle...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={handleBack}
            className="text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Puzzles
          </button>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">Goal:</span>
            <Board
              puzzle={puzzle}
              board={game.goalBoard}
              width={puzzle.width}
              height={puzzle.height}
              maxSize={120}
              interactive={false}
            />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-500">Moves: {moveCount}</span>
            {solved && <span className="text-green-600 font-bold">Solved!</span>}
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Game Board */}
        <div>
          <Board
            puzzle={puzzle}
            board={board}
            width={puzzle.width}
            height={puzzle.height}
            onDragPath={handleDragPath}
          />
        </div>

        {/* Info */}
        {puzzle.description && (
          <div className="mt-4 text-center text-slate-400 text-sm">{puzzle.description}</div>
        )}
      </div>
    </div>
  )
}
